/**
 * Legal & Fiscal Settings Page - Informations légales + Régime TVA
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Save, FileText, Calculator, Shield } from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import { useCompanySettings } from '@/hooks/useCompanySettings';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
  focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
  hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
  transition-all duration-300 outline-none text-sm`;

const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
  focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
  hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
  transition-all duration-300 outline-none cursor-pointer text-sm`;

export const LegalSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    company,
    loading,
    saving,
    error,
    updateField,
    saveCompany,
  } = useCompanySettings();

  const handleSave = async () => {
    await saveCompany();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
            <FileText className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error || t('settings.company.noCompany')}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton save */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{t('settings.legal.title')}</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="secondary"
          icon={<Save className="w-4 h-4" />}
        >
          {saving ? t('common.actions.saving') : t('common.actions.save')}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Informations légales */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-xl">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            {t('settings.legal.legalInfo')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('settings.legal.legalInfoDesc')}
          </p>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.legalForm')}
              </label>
              <select
                value={company.legalForm || ""}
                onChange={(e) => updateField("legalForm", e.target.value)}
                className={selectClasses}
              >
                <option value="">{t('common.select')}</option>
                <option value="Auto-entrepreneur">{t('settings.legal.legalForms.autoEntrepreneur')}</option>
                <option value="EI">{t('settings.legal.legalForms.ei')}</option>
                <option value="EIRL">{t('settings.legal.legalForms.eirl')}</option>
                <option value="EURL">{t('settings.legal.legalForms.eurl')}</option>
                <option value="SARL">{t('settings.legal.legalForms.sarl')}</option>
                <option value="SAS">{t('settings.legal.legalForms.sas')}</option>
                <option value="SASU">{t('settings.legal.legalForms.sasu')}</option>
                <option value="SA">{t('settings.legal.legalForms.sa')}</option>
                <option value="SNC">{t('settings.legal.legalForms.snc')}</option>
                <option value="SCS">{t('settings.legal.legalForms.scs')}</option>
                <option value="Association">{t('settings.legal.legalForms.association')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.shareCapital')} <span className="text-gray-400 text-xs">({t('settings.legal.inEuros')})</span>
              </label>
              <input
                type="number"
                value={company.capital || ""}
                onChange={(e) => updateField("capital", e.target.value)}
                className={inputClasses}
                placeholder="50000"
                min="0"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.legal.leaveEmptyIfNA')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.rcsCity')}
              </label>
              <input
                type="text"
                value={company.rcsCity || ""}
                onChange={(e) => updateField("rcsCity", e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.rcsCityPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.rcsNumber')}
              </label>
              <input
                type="text"
                value={company.rcsNumber || ""}
                onChange={(e) => updateField("rcsNumber", e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.placeholders.rcsNumber')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.ape')}
              </label>
              <input
                type="text"
                value={company.apeCode || ""}
                onChange={(e) => updateField("apeCode", e.target.value)}
                className={inputClasses}
                placeholder="6201Z"
                maxLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.paymentLegalNotice')}
              </label>
              <textarea
                value={company.legalNotesDefault || ""}
                onChange={(e) => updateField("legalNotesDefault", e.target.value)}
                className={`${inputClasses} resize-none`}
                placeholder={t('settings.legal.paymentLegalNoticePlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Régime de TVA */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <div className="p-2 bg-orange-100 rounded-xl">
              <Calculator className="w-5 h-5 text-orange-600" />
            </div>
            {t('settings.legal.vatRegime')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('settings.legal.vatRegimeDesc')}
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.taxRegime')}
              </label>
              <select
                value={company.vatRegime || "normal"}
                onChange={(e) => updateField("vatRegime", e.target.value)}
                className={selectClasses}
              >
                <option value="normal">{t('settings.legal.vatRegimes.normal')}</option>
                <option value="franchise_base">{t('settings.legal.vatRegimes.franchiseBase')}</option>
                <option value="exempt">{t('settings.legal.vatRegimes.exempt')}</option>
                <option value="intra_eu">{t('settings.legal.vatRegimes.intraEu')}</option>
                <option value="export">{t('settings.legal.vatRegimes.export')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {company.vatRegime === 'normal' && t('settings.legal.vatRegimeDescs.normal')}
                {company.vatRegime === 'franchise_base' && t('settings.legal.vatRegimeDescs.franchiseBase')}
                {company.vatRegime === 'exempt' && t('settings.legal.vatRegimeDescs.exempt')}
                {company.vatRegime === 'intra_eu' && t('settings.legal.vatRegimeDescs.intraEu')}
                {company.vatRegime === 'export' && t('settings.legal.vatRegimeDescs.export')}
              </p>
            </div>

            {(company.vatRegime === 'franchise_base' || company.vatRegime === 'exempt' || company.vatRegime === 'intra_eu' || company.vatRegime === 'export') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.legal.vatExemptionNotice')}
                </label>
                <input
                  type="text"
                  value={company.legalNotesDefault || ""}
                  onChange={(e) => updateField("legalNotesDefault", e.target.value)}
                  className={inputClasses}
                  placeholder={
                    company.vatRegime === 'franchise_base'
                      ? t('settings.legal.vatPlaceholders.franchiseBase')
                      : company.vatRegime === 'intra_eu'
                      ? t('settings.legal.vatPlaceholders.intraEu')
                      : company.vatRegime === 'export'
                      ? t('settings.legal.vatPlaceholders.export')
                      : t('settings.legal.vatPlaceholders.default')
                  }
                />
              </div>
            )}

            {/* Exigibilité TVA — visible uniquement si régime normal */}
            {(!company.vatRegime || company.vatRegime === 'normal') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.legal.vatExigibility')}
                </label>
                <select
                  value={company.vatExigibility || "ENCAISSEMENTS"}
                  onChange={(e) => updateField("vatExigibility", e.target.value)}
                  className={selectClasses}
                >
                  <option value="ENCAISSEMENTS">{t('settings.legal.vatExigibilities.ENCAISSEMENTS')}</option>
                  <option value="DEBITS">{t('settings.legal.vatExigibilities.DEBITS')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {company.vatExigibility === 'DEBITS'
                    ? t('settings.legal.vatExigibilityDescs.DEBITS')
                    : t('settings.legal.vatExigibilityDescs.ENCAISSEMENTS')
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section BTP — Assurances & Pénalités (S5) */}
      <div className={cardClasses}>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <div className="p-2 bg-amber-100 rounded-xl">
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
          {t('settings.legal.btp.title')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('settings.legal.btp.description')}
        </p>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Assurance décennale */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.insurerName')}
              </label>
              <input
                type="text"
                value={company.insurerName || ''}
                onChange={(e) => updateField('insurerName', e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.btp.insurerNamePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.insurerPolicyNumber')}
              </label>
              <input
                type="text"
                value={company.insurerPolicyNumber || ''}
                onChange={(e) => updateField('insurerPolicyNumber', e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.btp.insurerPolicyNumberPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.insurerCoverageZone')}
              </label>
              <input
                type="text"
                value={company.insurerCoverageZone || ''}
                onChange={(e) => updateField('insurerCoverageZone', e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.btp.insurerCoverageZonePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.insurerExpiryDate')}
              </label>
              <input
                type="date"
                value={company.insurerExpiryDate ? new Date(company.insurerExpiryDate).toISOString().split('T')[0] : ''}
                onChange={(e) => updateField('insurerExpiryDate', e.target.value)}
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.professionalInsurance')}
              </label>
              <input
                type="text"
                value={company.professionalInsurance || ''}
                onChange={(e) => updateField('professionalInsurance', e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.btp.professionalInsurancePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.tradeRegister')}
              </label>
              <input
                type="text"
                value={company.tradeRegister || ''}
                onChange={(e) => updateField('tradeRegister', e.target.value)}
                className={inputClasses}
                placeholder={t('settings.legal.btp.tradeRegisterPlaceholder')}
              />
            </div>
          </div>

          {/* Pénalités de retard structurées */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.lateFeeRate')} <span className="text-gray-400 text-xs">(% / mois)</span>
              </label>
              <input
                type="number"
                value={company.lateFeeRate ?? ''}
                onChange={(e) => updateField('lateFeeRate', e.target.value)}
                className={inputClasses}
                placeholder="3.00"
                min="0"
                max="100"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.legal.btp.lateFeeRateHelp')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.lateFeeFixedAmount')} <span className="text-gray-400 text-xs">(EUR)</span>
              </label>
              <input
                type="number"
                value={company.lateFeeFixedAmount ?? ''}
                onChange={(e) => updateField('lateFeeFixedAmount', e.target.value)}
                className={inputClasses}
                placeholder="40.00"
                min="0"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.legal.btp.lateFeeFixedAmountHelp')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.legal.btp.earlyPaymentDiscount')} <span className="text-gray-400 text-xs">(%)</span>
              </label>
              <input
                type="number"
                value={company.earlyPaymentDiscount ?? ''}
                onChange={(e) => updateField('earlyPaymentDiscount', e.target.value)}
                className={inputClasses}
                placeholder="0"
                min="0"
                max="100"
                step="0.01"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.legal.btp.earlyPaymentDiscountHelp')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalSettingsPage;
